import LoadingDots from 'components/UI/LoadingDots';
import { useDeviceStatusLoading } from 'features/Integrations/hooks/useDeviceStatusLoading';
import { useState } from 'react';
import { getDeviceStatusCode } from 'utils/devices/icon.utils';
import { capitalizeWord } from 'utils/strings/string.utils';
import { buildDeviceValueMap } from '../../utils/builder.utils';

// Simple, easy-to-edit lists of device fields to hide from the display.
// Modify these arrays here to control which parameters are not shown.
const HIDDEN_DEVICE_KEYS: string[] = [
  // e.g. 'data.depth1' or 'data.[0].id.value',
];
const HIDDEN_DEVICE_NAMES: string[] = [
  // e.g. 'Internal State',
  'Device Status', // Hide the raw status field, redundant with GetDeviceStatusCode badge display
];

// typed metadata shape for entries returned by `buildDeviceValueMap`.
type MetaData = {
  name: string;
  unitName?: string;
  units?: string;
  type?: string;
};

// Centralized mapping of device status codes to badge styles.
interface StatusStyle {
  bg: string;
  color: string;
  border: string;
}
const STATUS_COLOR_MAP: Record<string, StatusStyle> = {
  // Generic status codes (used across device types)
  // Blue dark enough for white text to clear WCAG AA (5.17:1) at this badge's 0.85rem.
  Loading: { bg: '#2563eb', color: '#ffffff', border: '#dfeaff' },
  Offline: { bg: '#000000', color: '#ffffff', border: '#ffffff' },
  Online: { bg: '#ffffff', color: '#000000', border: '#13ff0c' },
  Default: { bg: '#444444', color: '#ffffff', border: '#888888' },

  // Flood Stage status codes (from Flood devices / stageNodeStatus)
  Warning: { bg: '#d9ec01', color: '#000000', border: '#000000' },
  Flooding: { bg: '#ff0000', color: '#ffffff', border: '#000000' },
  Rising: { bg: '#ff8918', color: '#000000', border: '#000000' },
  Dropping: { bg: '#d9ec01', color: '#000000', border: '#000000' },
  Slow: { bg: '#d9ec01', color: '#000000', border: '#000000' },

  // RescAlert-specific status codes (from getRescAlertStatus)
  Maintenance: { bg: '#ff8918', color: '#000000', border: '#000000' },
  Alerting: { bg: '#ff0000', color: '#ffffff', border: '#000000' },
  Closed: { bg: '#ff0000', color: '#ffffff', border: '#000000' },
  Open: { bg: '#ffffff', color: '#000000', border: '#13ff0c' },
  Idle: { bg: '#ffffff', color: '#000000', border: '#13ff0c' },

  // USGS-specific status codes (rendered via USGSMarker state)
  //Not flooding/No Flood Stages use Generic Online
  Major: { bg: 'rgb(238, 170, 221)', color: '#000000', border: 'rgb(221, 85, 204)' },
  Moderate: { bg: 'rgb(238, 153, 136)', color: '#000000', border: 'rgb(221, 68, 51)' },
  Minor: { bg: 'rgb(255, 204, 136)', color: '#000000', border: 'rgb(255, 153, 51)' },
  Action: { bg: 'rgb(255, 255, 85)', color: '#000000', border: 'rgb(255, 238, 0)' },
};

// Local badge sizing (kept in-file so it's easy to tweak)
const BADGE_BASE_STYLE = { fontSize: '0.85rem', padding: '0.2rem 0.46rem' } as const;

/** Resolves a dotted key path (e.g. "data.depth1" or "data.[0].id.value") against a data[0] object. */
function resolveValue(key: string, latestData: Record<string, unknown>): unknown {
  const inner = key.replace(/^data\./, '').replace(/^\[0\]\./, '');
  const parts = inner.split('.');
  let cur: unknown = latestData;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

interface Props {
  /** The selected device object (from connectedDevices). */
  device: Record<string, unknown>;
  /** The caller-provided metadata map used to build the value map. */
  dataMaps: Record<string, unknown>;
}

/**
 * Displays the latest device status badge, timestamp, and all available data field values
 * for a selected device in the Global Actions IF condition builder.
 */
const DeviceLatestStatus = ({ device, dataMaps }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const isLoading = useDeviceStatusLoading(device);
  const latestData = (device?.data as unknown[])?.[0] as Record<string, unknown> | undefined;
  const valueMap = buildDeviceValueMap(device, dataMaps) as Record<string, MetaData>;

  // No point AND nothing on its way: there is genuinely nothing to show. While the
  // covering fetch is in flight the badge stays mounted and reads 'Loading'.
  if (!latestData && !isLoading) return null;

  const timestamp = latestData?.ts
    ? new Date(latestData.ts as number).toLocaleString(undefined, { timeZoneName: 'short' })
    : latestData?.date_created
      ? new Date(latestData.date_created as string).toLocaleString(undefined, {
          timeZoneName: 'short',
        })
      : null;

  const entries = (Object.entries(valueMap) as [string, MetaData][])
    .filter(([key, meta]) => {
      if (HIDDEN_DEVICE_KEYS.includes(key)) return false;
      if (HIDDEN_DEVICE_NAMES.includes(meta.name)) return false;
      return true;
    })
    .map(([key, meta]) => ({
      name: meta.name,
      units: meta.unitName ?? meta.units ?? '',
      value: resolveValue(key, latestData),
    }))
    .filter(({ value }) => value !== undefined && value !== null);

  const statusLabel: string = isLoading
    ? 'Loading'
    : capitalizeWord(getDeviceStatusCode(device) as string) || 'Offline';
  const statusStyle: StatusStyle = STATUS_COLOR_MAP[statusLabel] ?? STATUS_COLOR_MAP.Default;
  const summaryEntries = entries.slice(0, 3);

  return (
    <div className="global-action-builder-row mx-2 mb-2 rounded-3 p-3 d-flex flex-column gap-3">
      <div className="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between gap-3">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <span className="fw-semibold text-nowrap opacity-75" style={{ ...BADGE_BASE_STYLE }}>
            Latest:
          </span>
          <span
            className="global-action-builder-status-badge badge text-nowrap"
            style={{
              backgroundColor: statusStyle.bg,
              color: statusStyle.color,
              border: `1px solid ${statusStyle.border}`,
              ...BADGE_BASE_STYLE,
            }}
          >
            {isLoading ? (
              <span className="d-inline-flex align-items-center justify-content-center gap-1">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
                <LoadingDots />
              </span>
            ) : (
              statusLabel
            )}
          </span>
          {timestamp && (
            <span className="badge bg-secondary text-nowrap" style={{ ...BADGE_BASE_STYLE }}>
              {timestamp}
            </span>
          )}
        </div>

        <button
          type="button"
          className="btn btn-sm btn-outline-light align-self-start"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded ? 'Hide details' : `Show details (${entries.length})`}
        </button>
      </div>

      <div className="d-flex flex-wrap gap-2">
        {summaryEntries.map(({ name, units, value }, idx) => (
          <span
            key={`summary-${name}-${idx}`}
            className="global-action-builder-readonly-value badge text-nowrap"
            style={{ ...BADGE_BASE_STYLE }}
          >
            <span className="opacity-75">{name}:</span>{' '}
            <span className="fw-bold">
              {String(value)}
              {units ? ` ${units}` : ''}
            </span>
          </span>
        ))}
      </div>

      {expanded && (
        <div className="global-action-builder-editor rounded-3 p-3">
          <div className="row g-2">
            {entries.map(({ name, units, value }) => (
              <div key={name} className="col-12 col-md-6 col-xl-4">
                <div className="global-action-builder-readonly-value h-100 rounded-2 px-3 py-2">
                  <div className="small opacity-75">{name}</div>
                  <div className="fw-bold">
                    {String(value)}
                    {units ? ` ${units}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceLatestStatus;
